import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import { Alert, AlertTitle } from '../ui/alert';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import { useSession } from '../session-context';
import { apiErrorMessage, apiFetch, isProjectMembershipError, v1Path } from '../../lib/api';

export function ErrorAlert({ children }: { children: ReactNode }) {
  return (
    <Alert variant="destructive" className="border-0">
      <AlertCircle className="h-4 w-4" />
      <AlertTitle className="leading-snug">{children}</AlertTitle>
    </Alert>
  );
}

export function WorkPageError({
  error,
  projectId,
  onJoined,
}: {
  error: string | null;
  projectId?: string;
  onJoined?: () => void;
}) {
  if (!error) return null;
  if (projectId && isProjectMembershipError(error)) {
    return <NotProjectMember projectId={projectId} onJoined={onJoined} />;
  }
  return <ErrorAlert>{error}</ErrorAlert>;
}

function NotProjectMember({
  projectId,
  onJoined,
}: {
  projectId: string;
  onJoined?: () => void;
}) {
  const { ready, isOrgAdmin } = useSession();
  const [joining, setJoining] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [requested, setRequested] = useState(false);
  const [joinError, setJoinError] = useState<string | null>(null);

  useEffect(() => {
    if (!ready || !isOrgAdmin) return;
    let cancelled = false;
    setJoining(true);
    setJoinError(null);
    void apiFetch(v1Path(`/projects/${projectId}/join`), { method: 'POST' })
      .then(() => {
        if (cancelled) return;
        if (onJoined) {
          onJoined();
          return;
        }
        window.location.reload();
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setJoinError(
          apiErrorMessage(err, 'Failed to join this stream.'),
        );
        setJoining(false);
      });
    return () => {
      cancelled = true;
    };
  }, [ready, isOrgAdmin, projectId, onJoined]);

  async function requestAccess(): Promise<void> {
    setRequesting(true);
    setJoinError(null);
    try {
      await apiFetch(v1Path(`/projects/${projectId}/access-requests`), { method: 'POST' });
      setRequested(true);
    } catch (err) {
      setJoinError(apiErrorMessage(err, 'Failed to request access.'));
    } finally {
      setRequesting(false);
    }
  }

  if (isOrgAdmin) {
    return (
      <div className="max-w-lg space-y-3 border-t py-10">
        <p className="text-sm font-medium">Opening stream…</p>
        <p className="text-sm text-muted-foreground">
          {joining ? 'Adding you as a stream admin.' : 'Organization admins do not need to request access.'}
        </p>
        {joinError ? <p className="text-sm text-destructive">{joinError}</p> : null}
      </div>
    );
  }

  return (
    <div className="max-w-lg space-y-3 border-t py-10">
      <p className="text-sm font-medium">Not a stream member.</p>
      <p className="text-sm text-muted-foreground">
        Ask a stream admin for access. They will get an email when you request.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          size="sm"
          disabled={requesting || requested}
          onClick={() => void requestAccess()}
        >
          {requested ? 'Requested' : requesting ? 'Requesting…' : 'Request access'}
        </Button>
        <Button type="button" size="sm" variant="outline" asChild>
          <Link to="/projects">All streams</Link>
        </Button>
      </div>
      {joinError ? <p className="text-sm text-destructive">{joinError}</p> : null}
    </div>
  );
}

export function LoadingRows({ columns = 4 }: { columns?: number }) {
  const count = Number.isInteger(columns) && columns > 0 ? columns : 4;
  return (
    <div className="border-t" aria-busy="true" aria-label="Loading">
      {Array.from({ length: 8 }, (_, index) => (
        <div
          key={index}
          className="grid items-center gap-3 border-b py-2.5"
          style={{ gridTemplateColumns: `repeat(${count}, minmax(0, 1fr))` }}
        >
          {Array.from({ length: count }, (_, col) => (
            <Skeleton key={col} className="h-3 w-full max-w-[12rem]" />
          ))}
        </div>
      ))}
    </div>
  );
}

export function LoadingList({ rows = 5 }: { rows?: number }) {
  const count = Number.isInteger(rows) && rows > 0 ? rows : 5;
  return (
    <div
      className="divide-y overflow-hidden rounded-md border"
      aria-busy="true"
      aria-label="Loading"
    >
      {Array.from({ length: count }, (_, index) => (
        <div key={index} className="flex items-center gap-3 px-3 py-2.5">
          <Skeleton className="h-8 w-8 shrink-0 rounded-md" />
          <div className="min-w-0 flex-1 space-y-1.5">
            <Skeleton className="h-3.5 w-40" />
            <Skeleton className="h-3 w-64 max-w-full" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function ListEmpty({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="border-t py-16">
      <p className="text-sm font-medium">{title}</p>
      {description ? <p className="mt-1 text-sm text-muted-foreground">{description}</p> : null}
      {action ? <div className="mt-4">{action}</div> : null}
    </div>
  );
}

/** Full-area empty state: says there is nothing here and, when given, what to do next. */
export function BrandEmpty({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div role="status" className="flex flex-1 items-center justify-center py-16">
      <div className="flex max-w-sm flex-col items-center text-center">
        <p className="text-base font-semibold text-foreground">{title}</p>
        {description ? <p className="mt-1 text-sm text-muted-foreground">{description}</p> : null}
        {action ? <div className="mt-5">{action}</div> : null}
      </div>
    </div>
  );
}

export function PageTitle({
  title,
  count,
  description,
  action,
}: {
  title: string;
  count?: number;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div
      className={
        description
          ? 'flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between'
          : 'flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'
      }
    >
      <div className="min-w-0">
        <div className="flex min-w-0 items-baseline gap-2">
          <h1 data-slot="page-title" className="truncate text-[22px] font-semibold leading-7 tracking-tight">{title}</h1>
          {count != null ? (
            <span className="text-xs tabular-nums text-muted-foreground">{count}</span>
          ) : null}
        </div>
        {description ? <p className="mt-1 text-sm text-muted-foreground">{description}</p> : null}
      </div>
      {action}
    </div>
  );
}
